<template>
  <div class="flex flex-col gap-3 text-left" data-testid="payment-source-acceptance">
    <p class="text-xs text-neutral50 m-0">
      Define quais fontes do horário efetivo tornam o plantão elegível para
      <b>Antecipe</b> e <b>À vista</b>. Fonte desmarcada não bloqueia o registro nem a auditoria: o
      plantão segue no Fluxo.
    </p>

    <table class="w-full border border-gray-200 text-sm">
      <thead>
        <tr class="bg-lightGray30 text-left text-xs uppercase tracking-wide text-neutral50">
          <th class="px-3 py-2 font-bold">Fonte</th>
          <th
            v-for="modality in PAYMENT_MODALITIES"
            :key="modality"
            class="w-28 px-3 py-2 text-center font-bold"
          >
            {{ PAYMENT_MODALITY_LABELS[modality] }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="kind in ACCEPTANCE_SOURCE_KINDS"
          :key="kind"
          class="border-t border-gray-100"
          :data-testid="`payment-source-acceptance-row-${kind}`"
        >
          <td class="px-3 py-2 text-neutral50">{{ EFFECTIVE_SOURCE_KIND_LABELS[kind] }}</td>
          <td v-for="modality in PAYMENT_MODALITIES" :key="modality" class="px-3 py-2 text-center">
            <input
              type="checkbox"
              class="h-4 w-4 cursor-pointer accent-primary disabled:cursor-not-allowed"
              :aria-label="`${EFFECTIVE_SOURCE_KIND_LABELS[kind]} aceita para ${PAYMENT_MODALITY_LABELS[modality]}`"
              :checked="modelValue[modality].includes(kind)"
              :disabled="disabled"
              :data-testid="`payment-source-acceptance-${modality}-${kind}`"
              @change="onChange(modality, kind, $event)"
            />
          </td>
        </tr>
      </tbody>
    </table>

    <p
      v-if="missing.length"
      class="text-red-600 text-xs m-0"
      data-testid="payment-source-acceptance-error"
    >
      Selecione ao menos uma fonte para {{ missingLabel }}.
    </p>

    <p class="text-xs text-neutral50 m-0">
      Com check pelo App obrigatório na equipe, Registro de Atividade aprovado não torna o plantão
      elegível, mesmo marcado aqui. Registros manuais antigos (legado) são sempre aceitos.
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { EFFECTIVE_SOURCE_KIND_LABELS } from '@/constants/ds-attendance.constants'
import type { EffectiveSourceKind } from '@/constants/ds-attendance.constants'
import {
  PAYMENT_MODALITIES,
  PAYMENT_MODALITY_LABELS
} from '@/constants/payment-eligibility.constants'
import type { PaymentModality } from '@/types/payment-eligibility.types'
import {
  ACCEPTANCE_SOURCE_KINDS,
  modalitiesWithoutSource,
  setPaymentSourceAccepted,
  type PaymentSourceAcceptanceMatrix
} from './paymentSourceAcceptance'

const props = withDefaults(
  defineProps<{ modelValue: PaymentSourceAcceptanceMatrix; disabled?: boolean }>(),
  { disabled: false }
)

const emit = defineEmits<{ 'update:modelValue': [value: PaymentSourceAcceptanceMatrix] }>()

const missing = computed(() => modalitiesWithoutSource(props.modelValue))
const missingLabel = computed(() =>
  missing.value.map((modality) => PAYMENT_MODALITY_LABELS[modality]).join(' e ')
)

function onChange(modality: PaymentModality, kind: EffectiveSourceKind, event: Event): void {
  const accepted = (event.target as HTMLInputElement).checked
  emit('update:modelValue', setPaymentSourceAccepted(props.modelValue, modality, kind, accepted))
}
</script>
